import { useState, useMemo } from "react";
import {
  X, Trash, PencilSimple, FloppyDisk, ArrowsClockwise,
  Warning, EnvelopeSimple, FolderSimple, Envelope, Lightning, ToggleLeft, ToggleRight,
  ArrowSquareOut, Info,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";

const FREQUENCY_OPTIONS = [
  { value: "Day",   label: "Daily" },
  { value: "Week",  label: "Weekly" },
  { value: "Month", label: "Monthly" },
];
const WEEKDAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => ({ value: i, label: `${String(i).padStart(2, "0")}:00` }));

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
        {title}
      </p>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const FieldInput = (props) => (
  <input
    {...props}
    className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
  />
);

/**
 * View / edit / delete an existing automation.
 *
 * Modes:
 *   - "view"    → read-only summary
 *   - "edit"    → editable form
 *   - "confirm" → "Save changes?" modal-over-modal
 *
 * Props:
 *   automation:      the automation record
 *   projects:        full project list (for scope selector in edit mode)
 *   onSave(payload): persist the update (called from confirm)
 *   onDelete(id):    delete the automation
 *   onClose():       close the modal
 *   onToggle(id):    enable/disable
 *   busy:            "saving" | "deleting" | "toggling" | null
 */
export default function AutomationDetailModal({
  automation,
  projects = [],
  onSave,
  onDelete,
  onClose,
  onToggle,
  busy,
}) {
  const [mode, setMode] = useState("view"); // view | edit | confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Edit form state (initialised from the automation)
  const initial = useMemo(
    () => ({
      name: automation.name || "",
      description: automation.description || "",
      recipient_email: automation.config?.recipient_email || "",
      subject: automation.config?.subject || "",
      body: automation.config?.body || "",
      frequency: automation.config?.frequency || "Week",
      week_days: automation.config?.week_days?.length ? automation.config.week_days : ["Monday"],
      hour: automation.config?.hour ?? 9,
      folder_id: automation.config?.folder_id || "",
      project_name: automation.config?.project_name || "",
      destination_folder_id: automation.config?.destination_folder_id || "",
      destination_folder_name: automation.config?.destination_folder_name || "",
      only_with_attachments: automation.config?.only_with_attachments ?? true,
      project_scope: automation.project_scope || "all",
      project_ids: automation.project_ids || [],
    }),
    [automation]
  );
  const [form, setForm] = useState(initial);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isScheduledEmail = automation.template_type === "scheduled_email";
  const isFileAlert      = automation.template_type === "file_upload_alert";
  const isEmailReceived  = automation.template_type === "email_received_alert";

  const dirty =
    mode === "edit" &&
    JSON.stringify(form) !== JSON.stringify(initial);

  const buildPayload = () => {
    const config = {
      ...((!isEmailReceived) && { recipient_email: form.recipient_email }),
      ...(isScheduledEmail && {
        subject: form.subject,
        body: form.body,
        frequency: form.frequency,
        week_days: form.frequency === "Week" ? form.week_days : undefined,
        hour: form.hour,
      }),
      ...(isFileAlert && {
        folder_id: form.folder_id,
        project_name: form.project_name,
      }),
      ...(isEmailReceived && {
        destination_folder_id: form.destination_folder_id,
        destination_folder_name: form.destination_folder_name,
        only_with_attachments: form.only_with_attachments,
      }),
    };
    return {
      name: form.name.trim(),
      description: form.description,
      config,
      project_scope: isEmailReceived ? "all" : form.project_scope,
      project_ids: !isEmailReceived && form.project_scope === "specific" ? form.project_ids : [],
    };
  };

  const handleAskSave = () => {
    if (!form.name.trim()) return;
    if (!isEmailReceived && !form.recipient_email.trim()) return;
    if (isEmailReceived && !form.destination_folder_id.trim()) return;
    setMode("confirm");
  };

  const handleConfirmSave = async () => {
    const payload = buildPayload();
    try {
      await onSave(automation.id, payload);
      setMode("view");
    } catch {
      // toast handled upstream
      setMode("edit");
    }
  };

  const cancelEdit = () => {
    setForm(initial);
    setMode("view");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const tmplLabel =
    isEmailReceived ? "New Email Received"
    : isScheduledEmail ? "Scheduled Email"
    : "New File Upload Alert";
  const TmplIcon =
    isEmailReceived ? Envelope
    : isScheduledEmail ? EnvelopeSimple
    : FolderSimple;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid="automation-detail-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && mode === "view") onClose();
      }}
    >
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
              <TmplIcon size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {mode === "edit" ? form.name || "(unnamed)" : automation.name}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">{tmplLabel}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onToggle(automation.id)}
              disabled={busy === "toggling"}
              className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-primary transition-colors"
              data-testid="modal-toggle-btn"
              title={automation.enabled ? "Disable" : "Enable"}
            >
              {busy === "toggling" ? (
                <ArrowsClockwise size={14} className="animate-spin" />
              ) : automation.enabled ? (
                <ToggleRight size={20} className="text-emerald-500" weight="fill" />
              ) : (
                <ToggleLeft size={20} className="text-slate-300" weight="fill" />
              )}
              <span>{automation.enabled ? "Enabled" : "Disabled"}</span>
            </button>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors"
              data-testid="modal-close-btn"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
          {mode === "view" ? (
            <ViewMode automation={automation} projects={projects} />
          ) : (
            <EditMode
              form={form}
              set={set}
              isScheduledEmail={isScheduledEmail}
              isFileAlert={isFileAlert}
              isEmailReceived={isEmailReceived}
              projects={projects}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 flex-shrink-0 bg-slate-50">
          {mode === "view" ? (
            <>
              {showDeleteConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-600">Delete this automation?</span>
                  <button
                    onClick={() => onDelete(automation.id)}
                    className="text-xs font-semibold text-red-600 hover:underline"
                    data-testid="modal-confirm-delete-btn"
                  >
                    {busy === "deleting" ? "Deleting…" : "Yes, delete"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="text-xs text-slate-400 hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="modal-delete-btn"
                >
                  <Trash size={12} className="mr-1.5" /> Delete
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setMode("edit")}
                data-testid="modal-edit-btn"
              >
                <PencilSimple size={12} className="mr-1.5" /> Edit
              </Button>
            </>
          ) : (
            <>
              <button
                onClick={cancelEdit}
                className="text-sm text-slate-500 hover:text-slate-700"
                data-testid="modal-cancel-edit-btn"
              >
                Cancel
              </button>
              <Button
                size="sm"
                disabled={
                  !dirty ||
                  !form.name.trim() ||
                  (!isEmailReceived && !form.recipient_email.trim()) ||
                  (isEmailReceived && !form.destination_folder_id.trim()) ||
                  busy === "saving"
                }
                onClick={handleAskSave}
                data-testid="modal-save-btn"
              >
                <FloppyDisk size={12} className="mr-1.5" /> Save changes
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Confirm-save popup (modal-over-modal) */}
      {mode === "confirm" && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4"
          data-testid="confirm-save-modal"
        >
          <div className="bg-white border border-slate-200 shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                <Warning size={16} className="text-amber-500" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">Save changes?</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Your changes will overwrite the Azure Logic App workflow for{" "}
                  <span className="font-semibold text-slate-800">"{form.name}"</span>.
                </p>
                <p className="text-xs text-slate-500 leading-relaxed mt-2">
                  Are you sure you want to continue?
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode("edit")}
                disabled={busy === "saving"}
                data-testid="confirm-save-cancel-btn"
              >
                Keep editing
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmSave}
                disabled={busy === "saving"}
                data-testid="confirm-save-confirm-btn"
              >
                {busy === "saving" ? (
                  <>
                    <ArrowsClockwise size={12} className="mr-1.5 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <FloppyDisk size={12} className="mr-1.5" /> Yes, save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── View mode body ────────────────────────────────────────────────────────────
function ViewMode({ automation, projects }) {
  const cfg = automation.config || {};
  const isEmailReceived = automation.template_type === "email_received_alert";
  const projectNames =
    automation.project_scope === "specific"
      ? automation.project_ids
          ?.map((id) => projects.find((p) => p.id === id)?.name)
          .filter(Boolean)
      : null;

  return (
    <>
      {automation.description && (
        <Section title="Description">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{automation.description}</p>
        </Section>
      )}

      {isEmailReceived && (
        <Section title="Trigger">
          <p className="text-sm">
            New email arriving in your Outlook Inbox
            {cfg.only_with_attachments === false ? "" : " (only when it has attachments)"}.
          </p>
        </Section>
      )}

      {cfg.recipient_email && (
        <Section title="Recipient">
          <p className="font-mono text-sm">{cfg.recipient_email}</p>
        </Section>
      )}

      {cfg.subject && (
        <Section title="Email Subject">
          <p className="text-sm">{cfg.subject}</p>
        </Section>
      )}

      {cfg.body && (
        <Section title="Email Body">
          <pre className="text-xs whitespace-pre-wrap font-sans bg-slate-50 border border-slate-200 rounded p-2 max-h-32 overflow-y-auto">
            {cfg.body}
          </pre>
        </Section>
      )}

      {cfg.frequency && (
        <Section title="Schedule">
          <p className="text-sm">
            {FREQUENCY_OPTIONS.find((f) => f.value === cfg.frequency)?.label || cfg.frequency}
            {cfg.week_days?.length ? ` · ${cfg.week_days.join(", ")}` : ""}
            {cfg.hour !== undefined ? ` at ${String(cfg.hour).padStart(2, "0")}:00 AEST` : ""}
          </p>
        </Section>
      )}

      {cfg.project_name && (
        <Section title="OneDrive Folder">
          <p className="text-sm">{cfg.project_name}</p>
        </Section>
      )}

      {cfg.folder_id && (
        <Section title="OneDrive Folder ID">
          <p className="font-mono text-[11px] text-slate-500 break-all">{cfg.folder_id}</p>
        </Section>
      )}

      {isEmailReceived && (cfg.destination_folder_name || cfg.destination_folder_id) && (
        <Section title="Saves to OneDrive Folder">
          {cfg.destination_folder_name && (
            <p className="text-sm">{cfg.destination_folder_name}</p>
          )}
          {cfg.destination_folder_id && (
            <p className="font-mono text-[11px] text-slate-500 break-all mt-1">{cfg.destination_folder_id}</p>
          )}
        </Section>
      )}

      {!isEmailReceived && (
        <Section title="Project Scope">
          <p className="text-sm">
            {automation.project_scope === "all"
              ? "Applies to all current and future projects"
              : projectNames && projectNames.length > 0
              ? projectNames.join(", ")
              : `${automation.project_ids?.length || 0} project(s)`}
          </p>
        </Section>
      )}

      <Section title="Status">
        <div className="flex items-center gap-3 text-xs">
          <span className={automation.enabled ? "text-emerald-600 font-medium" : "text-slate-400"}>
            {automation.enabled ? "● Active" : "○ Disabled"}
          </span>
          {automation.last_run && (
            <span className="text-slate-500">
              Last run: {automation.last_run.status} ·{" "}
              {new Date(automation.last_run.start_time).toLocaleString("en-AU")}
            </span>
          )}
        </div>
      </Section>

      <Section title="Azure Logic App">
        <p className="font-mono text-[10px] text-slate-400 break-all">{automation.logic_app_name}</p>
      </Section>
    </>
  );
}

// ─── Edit mode body ────────────────────────────────────────────────────────────
function EditMode({ form, set, isScheduledEmail, isFileAlert, isEmailReceived, projects }) {
  return (
    <>
      <Field label="Automation name" required>
        <FieldInput
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. Weekly drawing reminder"
          data-testid="edit-form-name"
        />
      </Field>

      <Field label="Description">
        <FieldInput
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Optional"
          data-testid="edit-form-description"
        />
      </Field>

      {!isEmailReceived && (
        <Field label="Recipient email" required>
          <FieldInput
            type="email"
            value={form.recipient_email}
            onChange={(e) => set("recipient_email", e.target.value)}
            placeholder="engineer@company.com"
            data-testid="edit-form-recipient"
          />
        </Field>
      )}

      {isScheduledEmail && (
        <>
          <Field label="Subject">
            <FieldInput
              value={form.subject}
              onChange={(e) => set("subject", e.target.value)}
              data-testid="edit-form-subject"
            />
          </Field>

          <Field label="Email body (HTML supported)">
            <textarea
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              rows={4}
              data-testid="edit-form-body"
              className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
            />
          </Field>

          <Field label="Schedule">
            <div className="flex gap-2 flex-wrap">
              <select
                value={form.frequency}
                onChange={(e) => set("frequency", e.target.value)}
                className="text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="edit-form-frequency"
              >
                {FREQUENCY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {form.frequency === "Week" && (
                <select
                  value={form.week_days[0]}
                  onChange={(e) => set("week_days", [e.target.value])}
                  className="text-sm border border-slate-200 rounded px-2 py-1.5"
                  data-testid="edit-form-weekday"
                >
                  {WEEKDAYS.map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              )}
              <select
                value={form.hour}
                onChange={(e) => set("hour", Number(e.target.value))}
                className="text-sm border border-slate-200 rounded px-2 py-1.5"
                data-testid="edit-form-hour"
              >
                {HOURS.map((h) => (
                  <option key={h.value} value={h.value}>{h.label} AEST</option>
                ))}
              </select>
            </div>
          </Field>
        </>
      )}

      {isFileAlert && (
        <>
          <Field label="Project name (folder to monitor)" required>
            <FieldInput
              value={form.project_name}
              onChange={(e) => set("project_name", e.target.value)}
              data-testid="edit-form-project-name"
            />
          </Field>

          <Field label="OneDrive folder ID">
            <FieldInput
              value={form.folder_id}
              onChange={(e) => set("folder_id", e.target.value)}
              className="w-full text-xs border border-slate-200 rounded px-3 py-2 font-mono"
              data-testid="edit-form-folder-id"
            />
          </Field>
        </>
      )}

      {isEmailReceived && (
        <>
          <div className="bg-violet-50 border border-violet-100 rounded p-3 flex items-start gap-2">
            <Info size={13} className="text-violet-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-violet-700 leading-relaxed">
              Triggered by new emails in your Outlook Inbox. Applies globally — not per-project.
            </p>
          </div>
          <Field label="Destination OneDrive folder (display name)">
            <FieldInput
              value={form.destination_folder_name}
              onChange={(e) => set("destination_folder_name", e.target.value)}
              placeholder="e.g. Inbox Archive"
              data-testid="edit-form-dest-folder-name"
            />
          </Field>
          <Field label="OneDrive folder ID" required>
            <FieldInput
              value={form.destination_folder_id}
              onChange={(e) => set("destination_folder_id", e.target.value)}
              placeholder="Paste folder ID from Explore Files"
              className="w-full text-xs border border-slate-200 rounded px-3 py-2 font-mono"
              data-testid="edit-form-dest-folder-id"
            />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.only_with_attachments}
              onChange={(e) => set("only_with_attachments", e.target.checked)}
              className="w-3.5 h-3.5 accent-primary"
              data-testid="edit-form-only-attachments"
            />
            <span className="text-xs text-slate-700">Only trigger when the email has attachments</span>
          </label>
        </>
      )}

      {!isEmailReceived && (
        <Field label="Project scope">
          <div className="space-y-2">
            {[
              { v: "all", label: "All projects" },
              { v: "specific", label: "Specific projects" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => set("project_scope", opt.v)}
                className={`w-full text-left px-3 py-2 border rounded text-sm transition-colors ${
                  form.project_scope === opt.v
                    ? "border-primary bg-primary/5 text-slate-800"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`edit-scope-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {form.project_scope === "specific" && (
            <div className="space-y-1 mt-3 pl-1">
              {projects.length === 0 ? (
                <p className="text-xs text-slate-400">No projects available.</p>
              ) : (
                projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.project_ids.includes(p.id)}
                      onChange={(e) =>
                        set(
                          "project_ids",
                          e.target.checked
                            ? [...form.project_ids, p.id]
                            : form.project_ids.filter((id) => id !== p.id)
                        )
                      }
                      className="w-3.5 h-3.5 accent-primary"
                    />
                    <span className="text-sm text-slate-700">{p.name}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </Field>
      )}
    </>
  );
}
